import { useEffect, useState } from "react";
import { api, formatRupiah, errMsg } from "@/lib/api";
import { Plus, X } from "lucide-react";

const STAGES = ["Lead","Konsultasi","Survey","Quotation","Negosiasi","Deal","Lost"];
const LOST_REASONS = ["Harga","Tanggal Bentrok","Pilih Kompetitor","Tidak Respons","Lainnya"];

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ client_name: "", phone: "", estimated_value: 0, source: "Instagram", segment: "Wedding", stage: "Lead", event_date: "" });

  async function load() {
    const { data } = await api.get("/leads");
    setLeads(data);
  }
  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, estimated_value: Number(form.estimated_value) || 0 });
      setOpen(false); setForm({ client_name: "", phone: "", estimated_value: 0, source: "Instagram", segment: "Wedding", stage: "Lead", event_date: "" });
      await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  async function moveStage(lead, newStage) {
    let patch = { stage: newStage };
    if (newStage === "Lost") {
      const reason = window.prompt("Alasan Lost?\n" + LOST_REASONS.join(" / "), "Harga");
      if (!reason) return;
      patch.lost_reason = reason;
    }
    await api.patch(`/leads/${lead.id}`, patch);
    await load();
  }

  return (
    <div className="p-4 sm:p-8">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">CRM Pipeline</h1>
          <p className="text-sm text-[#536263]">Alur lead → deal, lengkap dengan capacity gate &amp; alasan lost</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="crm-add-lead-btn"><Plus size={18} />Tambah Lead</button>
      </header>

      <div className="scroll-horizontal pb-4">
        <div className="flex gap-4 min-w-max" data-testid="crm-kanban">
          {STAGES.map(s => (
            <div key={s} className="kanban-col w-72">
              <div className="flex items-center justify-between mb-2 px-1">
                <h3 className="font-serif-display font-semibold text-[#1A2627]">{s}</h3>
                <span className="chip bg-[#F2EEE7] text-[#536263]">{leads.filter(l => l.stage === s).length}</span>
              </div>
              <div className="space-y-2 min-h-[200px]">
                {leads.filter(l => l.stage === s).map(l => (
                  <div key={l.id} className="card-editorial p-3" data-testid={`lead-card-${l.id}`}>
                    <div className="font-medium text-sm">{l.client_name}</div>
                    <div className="text-xs text-[#8A999A] mt-1">{l.phone}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="chip bg-[#F7EFE8] text-[#B87333]">{l.segment}</span>
                      <span className="font-mono-num text-sm font-semibold text-[#0F5257]">{formatRupiah(l.estimated_value)}</span>
                    </div>
                    <div className="text-[11px] text-[#8A999A] mt-1">Sumber: {l.source}</div>
                    {l.lost_reason && <div className="text-[11px] text-red-700 mt-1">Lost: {l.lost_reason}</div>}
                    <select onChange={e => moveStage(l, e.target.value)} value={l.stage}
                      className="mt-2 w-full text-xs border border-[#E2DDD3] rounded px-2 py-1.5"
                      data-testid={`lead-stage-${l.id}`}>
                      {STAGES.map(ss => <option key={ss}>{ss}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onSubmit={create} onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-4" data-testid="lead-form">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl font-bold">Lead Baru</h3>
              <button type="button" onClick={() => setOpen(false)}><X /></button>
            </div>
            <input required placeholder="Nama Klien" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})}
              className="w-full border border-[#E2DDD3] rounded-lg px-3 py-2.5 h-11" data-testid="lead-name-input" />
            <input placeholder="No. WhatsApp" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
              className="w-full border border-[#E2DDD3] rounded-lg px-3 py-2.5 h-11" />
            <input type="number" placeholder="Estimasi nilai (Rp)" value={form.estimated_value} onChange={e => setForm({...form, estimated_value: e.target.value})}
              className="w-full border border-[#E2DDD3] rounded-lg px-3 py-2.5 h-11" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 py-2.5 h-11">
                {["Instagram","Referral","Expo","#KilasRuangPuade","Repeat"].map(x => <option key={x}>{x}</option>)}
              </select>
              <select value={form.segment} onChange={e => setForm({...form, segment: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 py-2.5 h-11">
                {["Wedding","Corporate","Brand Activation","Expo","Lainnya"].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
            <input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})}
              className="w-full border border-[#E2DDD3] rounded-lg px-3 py-2.5 h-11" />
            {err && <div className="text-sm text-red-700">{err}</div>}
            <button type="submit" className="btn-primary w-full" data-testid="lead-submit">Simpan</button>
          </form>
        </div>
      )}
    </div>
  );
}
